import { useMemo } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Award, Lock, FlaskConical, GraduationCap } from "lucide-react";
import { progress } from "@/lib/progress";
import { imgUrl } from "@/lib/robotLab";
import { courseImg } from "@/lib/advancedCourses";
import { EduNav } from "@/components/robotlab/EduNav";

const Ring = ({ pct }) => (
  <div className="relative w-36 h-36 shrink-0" data-testid="progress-ring">
    <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90"><circle cx="60" cy="60" r="52" fill="none" stroke="#ffffff1f" strokeWidth="12" />
      <circle cx="60" cy="60" r="52" fill="none" stroke="#4FD1C5" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(pct / 100) * 326.7} 326.7`} className="transition-[stroke-dasharray] duration-700" /></svg>
    <div className="absolute inset-0 grid place-items-center text-center"><div><p className="font-['Outfit'] font-black text-3xl" data-testid="progress-pct">{pct}%</p><p className="text-[11px] text-slate-300">overall</p></div></div>
  </div>
);

const Row = ({ to, img, title, sub, at, testid }) => (
  <Link to={to} data-testid={testid} className="group flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-3 hover:border-[#007A8A] hover:-translate-y-0.5 transition-[transform,border-color] duration-300">
    <img src={img} alt="" loading="lazy" className="w-16 h-12 rounded-xl object-cover" />
    <div className="flex-1 min-w-0"><p className="font-bold text-sm text-[#0A192F] truncate">{title}</p><p className="text-xs text-slate-500">{at ? `Finished ${new Date(at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : sub}</p></div>
    {at ? <CheckCircle2 className="w-5 h-5 text-[#3BB273] shrink-0" data-testid={`${testid}-done`} /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
  </Link>
);

const Section = ({ icon: Icon, title, count, children, testid }) => (
  <section data-testid={testid}>
    <div className="flex items-end justify-between gap-3"><h2 className="font-['Outfit'] font-extrabold text-2xl text-[#0A192F] flex items-center gap-2"><Icon className="w-6 h-6 text-[#007A8A]" />{title}</h2><span className="text-sm font-bold text-slate-500" data-testid={`${testid}-count`}>{count}</span></div>
    <div className="mt-4 grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>
  </section>
);

export default function MyProgress() {
  const p = useMemo(progress, []);
  return (
    <div className="bg-[#F4F7FC] min-h-screen" data-testid="my-progress-page">
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
          <EduNav />
          <div className="mt-8 flex flex-wrap items-center gap-8">
            <Ring pct={p.pct} />
            <div className="flex-1 min-w-[240px]">
              <h1 className="font-['Outfit'] font-black text-4xl sm:text-5xl">My Progress</h1>
              <p className="mt-2 text-slate-300 max-w-xl">Every Robot Lab demo and free course lesson you finish is saved on this device — no login needed.</p>
              <div className="mt-4 flex flex-wrap gap-2 text-sm font-bold">
                <span className="rounded-full bg-white/10 border border-white/15 px-3 py-1" data-testid="progress-labs-chip">{`${p.labsDone} / ${p.labs.length} labs`}</span>
                <span className="rounded-full bg-white/10 border border-white/15 px-3 py-1" data-testid="progress-lessons-chip">{`${p.lessonsDone} / ${p.courses.length} free lessons`}</span>
              </div>
            </div>
            <div className={`rounded-3xl p-5 w-full sm:w-72 ${p.allLabs ? "bg-[#FFD166] text-[#0A192F]" : "bg-white/10 border border-white/15"}`} data-testid="progress-certificate-card">
              {p.allLabs ? <Award className="w-8 h-8" /> : <Lock className="w-8 h-8 text-slate-300" />}
              <p className="mt-2 font-['Outfit'] font-extrabold text-lg">Robot Lab Certificate</p>
              <p className={`text-sm ${p.allLabs ? "" : "text-slate-300"}`}>{p.allLabs ? "All 15 labs done — claim your certificate!" : `Finish all 15 labs to unlock (${p.labs.length - p.labsDone} to go).`}</p>
              <Link to="/robot-lab/certificate" className={`mt-3 inline-block rounded-full px-4 py-2 text-sm font-bold ${p.allLabs ? "bg-[#0A192F] text-white" : "bg-white/10 text-white"}`} data-testid="progress-certificate-link">{p.allLabs ? "Get certificate" : "Preview"}</Link>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12 space-y-12">
        <Section icon={FlaskConical} title="Robot Lab demos" count={`${p.labsDone} / ${p.labs.length}`} testid="progress-labs">
          {p.labs.map((l) => <Row key={l.id} to={`/robot-lab/${l.id}`} img={imgUrl(l.img)} title={l.title} sub={`${l.cat} · ${l.mins} min`} at={l.at} testid={`progress-lab-${l.id}`} />)}
        </Section>
        <Section icon={GraduationCap} title="Advanced Courses · free lessons" count={`${p.lessonsDone} / ${p.courses.length}`} testid="progress-courses">
          {p.courses.map((c) => <Row key={c.id} to={`/advanced-courses/${c.id}/lesson`} img={courseImg(c)} title={c.title} sub="Free first lesson" at={c.at} testid={`progress-course-${c.id}`} />)}
        </Section>
      </div>
    </div>
  );
}
